import React, { FormEvent } from 'react';
import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import { MONTH_OPTIONS } from '../../data/contactTopics';
import { contact as contactInfo } from '../../data/contact';
import type { ContactTopic } from '../../data/contactTopics';
import type { EnquiryErrors } from '../../utils/enquiry';
interface ContactDetailsFormProps {
  topic: ContactTopic;
  answers: Record<string, string>;
  contact: {
    name: string;
    email: string;
    phone: string;
    message: string;
    consent: boolean;
  };
  errors: EnquiryErrors;
  submitting: boolean;
  failed: boolean;
  onAnswer: (id: string, value: string) => void;
  onContact: (key: 'name' | 'email' | 'phone' | 'message' | 'consent', value: string | boolean) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}
const field = 'mt-1.5 block w-full rounded-xl border-2 bg-white px-4 type-field text-ink placeholder:text-muted outline-none transition-colors duration-150 focus:border-brand';

// Final step: questions specific to the chosen topic, then name, email and message.
export function ContactDetailsForm({
  topic,
  answers,
  contact,
  errors,
  submitting,
  failed,
  onAnswer,
  onContact,
  onSubmit
}: ContactDetailsFormProps) {
  const months = upcomingMonths(12);
  const border = (key: keyof EnquiryErrors) => errors[key] ? 'border-danger' : 'border-line';
  return <form noValidate onSubmit={onSubmit} className="space-y-8" data-id="element-103" data-mp="2tcdxv53l7x6x">
      {failed ? <div role="alert" className="flex gap-3 rounded-xl border-2 border-danger bg-white p-4 type-card-desc text-ink" data-id="element-104" data-mp="3qxs524mtocdt">
          <AlertCircleIcon className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" data-id="element-105" data-mp="3m1bfr73vzrz5" />
          <p data-id="element-106" data-mp="3h6p5j79jk5gn">
            We could not send your message. Check your connection and try again, or write to{' '}
            <a href={contactInfo.emailLink} className="font-semibold underline" data-id="element-107" data-mp="3c7cf6cjgog9b">
              {contactInfo.email}
            </a>
            .
          </p>
        </div> : null}

      {topic.fields.length ? <fieldset data-id="element-108" data-mp="230kepcr2qk3x">
          <legend className="type-h5 text-ink" data-id="element-109" data-mp="230ajnar69mrd">A few quick questions</legend>
          <div className="mt-4 grid gap-5 sm:grid-cols-2" data-id="element-110" data-mp="27zneois76051">
            {topic.fields.map(f => {
          const id = `cw-${f.id.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          const options = f.options?.[0] === MONTH_OPTIONS ? [...months, 'Not sure yet'] : f.options ?? [];
          return <div key={f.id} data-id="element-111" data-mp="1k9jbgcix82ek">
                  <label htmlFor={id} className="type-form-label text-ink" data-id="element-112" data-mp="1k8hrfijxg5kp">
                    {f.label}
                  </label>
                  {f.type === 'select' ? <select id={id} value={answers[f.id] ?? ''} onChange={e => onAnswer(f.id, e.target.value)} className={`${field} h-12 border-line`} data-id="element-113" data-mp="1acwva11jhziv">
                      <option value="" data-id="element-114" data-mp="1fcsxpvul9iwb">Choose…</option>
                      {options.map(option => <option key={option} value={option} data-id="element-115" data-mp="39oz9avrea1p5">
                          {option}
                        </option>)}
                    </select> : <input id={id} type="text" placeholder={f.placeholder} value={answers[f.id] ?? ''} onChange={e => onAnswer(f.id, e.target.value)} className={`${field} h-12 border-line`} data-id="element-116" data-mp="t2sy2z052t69" />}
                </div>;
        })}
          </div>
        </fieldset> : null}

      <fieldset data-id="element-117" data-mp="1grlx54c25h53">
        <legend className="type-h5 text-ink" data-id="element-118" data-mp="1gr3sbjbbxulk">How can we reach you?</legend>
        <div className="mt-4 grid gap-5 sm:grid-cols-2" data-id="element-119" data-mp="1btalco27cwk8">
          <TextField id="cw-name" name="name" label="Full name" autoComplete="name" value={contact.name} error={errors.name} className={border('name')} onChange={v => onContact('name', v)} data-id="element-120" data-mp="1bssl7gcf5y71" />
          <TextField id="cw-email" name="email" type="email" label="Email" autoComplete="email" value={contact.email} error={errors.email} className={border('email')} onChange={v => onContact('email', v)} data-id="element-121" data-mp="1lrzvfunuxcpx" />
          <div className="sm:col-span-2" data-id="element-122" data-mp="3nko26dzpmiag">
            <TextField id="cw-phone" name="phone" type="tel" label="WhatsApp number (optional)" autoComplete="tel" placeholder="+00 000 000 0000" value={contact.phone} error={errors.phone} className={border('phone')} onChange={v => onContact('phone', v)} data-id="element-123" data-mp="2em64x7td59k2" />
          </div>
          <div className="sm:col-span-2" data-id="element-124" data-mp="2ohzld2a7dn74">
            <label htmlFor="cw-message" className="type-form-label text-ink" data-id="element-125" data-mp="2ske6jt1cgz1k">
              Anything else we should know? (optional)
            </label>
            <textarea id="cw-message" rows={4} value={contact.message} onChange={e => onContact('message', e.target.value)} className={`${field} resize-y border-line py-3`} data-id="element-126" data-mp="1vtbyj29t2prg" />
          </div>
        </div>
      </fieldset>

      <div data-id="element-127" data-mp="29n398dz9wfor">
        <label className="flex cursor-pointer items-start gap-3 type-card-desc" data-id="element-128" data-mp="29mtg4hixlseu">
          <input name="consent" type="checkbox" checked={contact.consent} onChange={e => onContact('consent', e.target.checked)} className="mt-1 h-5 w-5 shrink-0 rounded border-2 border-line accent-brand" aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'cw-consent-error' : undefined} data-id="element-129" data-mp="1d4x5k9fxh1o0" />
          <span data-id="element-130" data-mp="10ljptj089omg">I agree that ES World can contact me about my enquiry, as explained in the privacy policy.</span>
        </label>
        {errors.consent ? <p id="cw-consent-error" className="mt-1.5 type-small font-semibold text-danger" data-id="element-131" data-mp="vp2rxx387vw6">
            {errors.consent}
          </p> : null}
      </div>

      <button type="submit" disabled={submitting} className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-[15px] bg-brand px-7 type-button font-bold text-white transition-colors duration-150 hover:bg-brand-hover active:bg-brand-press disabled:cursor-wait disabled:bg-brand-press sm:w-auto" data-id="element-132" data-mp="lwdj79z28w4j">
        {submitting ? <>
            <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-133" data-mp="2zcdto1lgu02" />
            Sending
          </> : topic.submitLabel}
      </button>
    </form>;
}
interface TextFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
  error?: string;
  className: string;
}
function TextField({
  id,
  name,
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  placeholder,
  error,
  className
}: TextFieldProps) {
  return <div data-id="element-134" data-mp="h0szub6809uk">
      <label htmlFor={id} className="type-form-label text-ink" data-id="element-135" data-mp="h0j8aj5j1umr">
        {label}
      </label>
      <input id={id} name={name} type={type} autoComplete={autoComplete} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} className={`${field} h-12 ${className}`} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} data-id="element-136" data-mp="2bwrse45ywty" />
      {error ? <p id={`${id}-error`} className="mt-1.5 type-small font-semibold text-danger" data-id="element-137" data-mp="2kgwn39e2sfv8">
          {error}
        </p> : null}
    </div>;
}
function upcomingMonths(count: number) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric'
  });
  const now = new Date();
  return Array.from({
    length: count
  }, (_, i) => formatter.format(new Date(now.getFullYear(), now.getMonth() + i, 1)));
}